import React, { useCallback, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme, Colors } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { GenericButton } from '@app/core';

import classnames from 'classnames';

export type ChipColor = Colors;

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: theme.marginBase,
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
  },
  alwaysVisibleRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    ...theme.fonts.label,
    flex: 1,
  },
  button: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  toggleLabel: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  caret: {
    marginLeft: theme.marginBase,
    color: theme.colors.blueAccessible,
  },
  foldableContent: {
    width: '100%',
    transition: 'max-height 0.5s ease-in-out',
    maxHeight: 1000,
    overflow: 'hidden',
  },
  folded: {
    maxHeight: 0,
  },
  content: {
    paddingTop: theme.marginBase * 2,
  },
}));

interface Props {
  label?: string;
  toggleLabel?: string;
  className?: string;
  children: React.ReactNode;
}

export const FoldableParagraphSection: React.FC<Props> = ({
  label,
  toggleLabel,
  children,
}) => {
  const [isFolded, setIsFolded] = useState(true);
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const onClick = useCallback(() => setIsFolded(!isFolded), [isFolded]);

  return (
    <div className={classes.container}>
      <div className={classes.alwaysVisibleRow}>
        {label && <p className={classes.label}>{t(label)}</p>}
        <GenericButton
          className={classes.button}
          onClick={onClick}
          type="button"
        >
          {toggleLabel && (
            <span className={classes.toggleLabel}>{t(toggleLabel)}</span>
          )}
          <i className={classnames('ri-arrow-down-s-line', classes.caret)} />
        </GenericButton>
      </div>
      <div
        className={classnames(classes.foldableContent, {
          [classes.folded]: isFolded,
        })}
      >
        <div className={classes.content}>{children}</div>
      </div>
    </div>
  );
};
